import * as React from 'react';
import { Search } from 'lucide-react';
import { cn } from '../../lib/utils';
import { Input } from '../../components/ui/input';
import { Skeleton } from '../../components/ui/skeleton';
import { TASHKENT_TZ } from '@/lib/datetime';

/**
 * Общие куски списков «Заказы», «Платежи», «Подписки» (макет «Пользователи» + «Карточка»):
 * поле поиска с иконкой, скелет строк, карточка-секция в боковой панели, формат дат.
 */

/** «31.10.2026, 14:07» по Ташкенту; пусто → «—». */
export function formatDateTime(date: Date | string | null | undefined): string {
  if (!date) return '—';
  const d = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(d.getTime())) return '—';
  return new Intl.DateTimeFormat('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: TASHKENT_TZ,
  }).format(d);
}

/** Деньги: «49 900 сум» для UZS, иначе Intl. */
export function formatAmount(value: number, currency: string = 'UZS'): string {
  if (!currency || currency.toUpperCase() === 'UZS') {
    return `${String(Math.round(value)).replace(/\B(?=(\d{3})+(?!\d))/g, ' ')} сум`;
  }
  try {
    return new Intl.NumberFormat('ru-RU', { style: 'currency', currency, maximumFractionDigits: 0 }).format(value);
  } catch {
    return `${value} ${currency}`;
  }
}

/** Короткий id для таблицы: первые 8 символов uuid. */
export const shortId = (id: string | null | undefined) => (id ? `${id.slice(0, 8)}…` : '—');

/** Поле поиска с лупой; id/aria приходят из FormField. */
export const SearchInput = React.forwardRef<HTMLInputElement, React.InputHTMLAttributes<HTMLInputElement>>(
  ({ className, ...props }, ref) => (
    <span className="relative block">
      <Search aria-hidden className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-text-3" />
      <Input ref={ref} type="search" className={cn('pl-9', className)} {...props} />
    </span>
  ),
);
SearchInput.displayName = 'SearchInput';

/** Скелет строк таблицы на время загрузки. */
export function RowsSkeleton({ rows = 6, label }: { rows?: number; label: string }) {
  return (
    <div className="flex flex-col" aria-busy="true" aria-label={label}>
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} className="flex items-center gap-6 border-t border-border-soft px-[18px] py-3.5">
          <Skeleton className="h-4 w-20" />
          <div className="flex flex-col gap-1.5">
            <Skeleton className="h-4 w-36" />
            <Skeleton className="h-3 w-28" />
          </div>
          <Skeleton className="h-4 w-24" />
          <Skeleton className="ml-auto h-4 w-20" />
          <Skeleton className="h-6 w-24 rounded-full" />
        </div>
      ))}
    </div>
  );
}

/** Скелет содержимого боковой панели. */
export function SheetSkeleton() {
  return (
    <div className="flex flex-col gap-3" aria-busy="true" aria-label="Загрузка">
      <Skeleton className="h-24 w-full rounded-card" />
      <Skeleton className="h-40 w-full rounded-card" />
      <Skeleton className="h-24 w-full rounded-card" />
    </div>
  );
}

/** Карточка-секция в боковой панели деталей (макет «Карточка пользователя»). */
export function SheetSection({ title, children, className }: { title: string; children: React.ReactNode; className?: string }) {
  return (
    <section
      aria-label={title}
      className={cn('flex flex-col gap-3 rounded-card border border-border-soft bg-surface px-4 py-3.5 shadow-card', className)}
    >
      <h3 className="m-0 font-display text-[15px] font-bold">{title}</h3>
      <dl className="m-0 grid grid-cols-1 gap-x-4 gap-y-3 sm:grid-cols-2">{children}</dl>
    </section>
  );
}

/** Пара «подпись — значение» внутри SheetSection. */
export function SheetItem({
  label,
  children,
  mono,
  wide,
}: {
  label: string;
  children: React.ReactNode;
  mono?: boolean;
  /** На всю ширину секции (длинные id). */
  wide?: boolean;
}) {
  return (
    <div className={cn('flex min-w-0 flex-col gap-1', wide && 'sm:col-span-2')}>
      <dt className="text-xs text-text-2">{label}</dt>
      <dd className={cn('m-0 text-sm text-text', mono && 'break-all font-mono text-[13px]')}>{children}</dd>
    </div>
  );
}
